import { Language, PropertyPicker } from '@marketertechnologies/property-picker';
import '@marketertechnologies/property-picker/dist/index.min.css';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import { getParentHref } from '~/helpers/getParentHref';
import { usePropertyExplorerProjectData } from '~/hooks/usePropertyExplorerProjectData';
import { usePropertyExplorerScenes } from '~/hooks/usePropertyExplorerScenes';
import { usePropertyExplorerSplashScreen } from '~/hooks/usePropertyExplorerSplashScreen';
import { getFloorplansPreviewData } from '~/queries/getFloorplansPreviewData';
import { getUnitsAssetsData } from '~/queries/getUnitsAssetsData';
import { getIsInFrame } from '~/utils/getIsInIframe';

import { useCleanup } from './useCleanup';
import { useIsLoading } from './useIsLoading';

const searchParams = new URLSearchParams(window.location.search);

const previewMode = !!searchParams.get('previewMode');
const startView = searchParams.get('startView') ?? undefined;

const PropertyExplorer = () => {
  const { uuid } = useParams();

  const [searchParams] = useSearchParams();
  const language = (searchParams.get('lang') || 'no') as Language;

  const splashScreenData = usePropertyExplorerSplashScreen({
    sceneUuid: startView,
  });
  const projectData = usePropertyExplorerProjectData({
    initialLanguage: language,
  });

  const data = {
    language,
    ...splashScreenData,
    ...projectData,
  };
  const projectUuid = data?.project?.uuid ?? uuid!;

  const { scenes } = usePropertyExplorerScenes({
    projectUuid,
    sceneUuid: startView,
  });

  const isLoading = useIsLoading();

  useCleanup(!isLoading && projectData ? projectData : undefined);

  const getFloorplansPreview = useCallback(
    (unitsUuidList: string[]) => getFloorplansPreviewData({ projectUuid, unitsUuidList }),
    [projectUuid],
  );

  const getUnitsAssets = useCallback(
    (unitsUuidList?: string[]) => getUnitsAssetsData({ projectUuid, unitsUuidList }),
    [projectUuid],
  );

  return (
    <PropertyPicker
      key={projectUuid}
      isWrapped={getIsInFrame()}
      showSplashScreen={splashScreenData && !projectData}
      project={data?.project}
      rendererType={data?.rendererType ?? data?.settings?.rendererType}
      settings={data?.settings}
      firstFrame={splashScreenData?.firstFrame}
      videos={splashScreenData?.videos}
      levels={splashScreenData?.levels}
      scenes={scenes}
      theme={data?.theme}
      lang={data?.language}
      parentEndpoint={getParentHref()}
      {...{ previewMode, startView, getFloorplansPreview, getUnitsAssets }}
    />
  );
};

export default PropertyExplorer;
